/**
 * Admin reports browser. The moderation-queue surface shows OPEN
 * reports prioritized for action; this view is the historical browser
 * across all statuses (open / withdrawn / dismissed / confirmed) for
 * back-fill investigations and audit context.
 *
 * Reuses the existing `useQueueQuery` shape via `/admin/queue?status=`
 * — the moderation-service backend already accepts the filter; this
 * route just surfaces a different status default + a status switcher.
 * No new backend wiring needed.
 */

import { Badge, type BadgeVariant } from '@funkihoo/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../api/client.js';
import { type ReportRow, useResolveReportMutation } from '../api/moderation.js';

type StatusFilter = 'open' | 'withdrawn' | 'dismissed' | 'confirmed';

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'confirmed', label: 'Confirmed' },
];

const reportsKeys = {
  all: ['admin-reports'] as const,
  list: (status: StatusFilter) => [...reportsKeys.all, 'list', status] as const,
} as const;

function useReportsQuery(status: StatusFilter) {
  return useQuery({
    queryKey: reportsKeys.list(status),
    queryFn: () =>
      apiFetch<{ reports: ReportRow[] }>({
        method: 'GET',
        url: `/admin/queue?status=${status}&limit=100`,
      }),
  });
}

export function ReportsRoute() {
  const [filter, setFilter] = useState<StatusFilter>('open');
  const query = useReportsQuery(filter);

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">Reports</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Historical browser across every report status. For action-oriented triage of open reports,
          use the Moderation queue. Resolve actions are available here too — same backend.
        </p>
      </header>

      <div className="flex items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setFilter(t.value)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
              filter === t.value
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            {t.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => query.refetch()}
          className="ml-auto rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          Refresh
        </button>
      </div>

      {query.isLoading && <p className="text-sm text-neutral-500">Loading…</p>}
      {query.isError && (
        <p className="text-sm text-danger-600">
          Could not load reports. moderation-service down on :8008?
        </p>
      )}
      {query.data && query.data.reports.length === 0 && (
        <p className="text-sm text-neutral-500">No reports in this view.</p>
      )}

      {query.data && query.data.reports.length > 0 && (
        <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
          {query.data.reports.map((r) => (
            <li key={r.id}>
              <ReportRowItem row={r} canResolve={filter === 'open' || filter === 'withdrawn'} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReportRowItem({ row, canResolve }: { row: ReportRow; canResolve: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const resolve = useResolveReportMutation();

  return (
    <div className="px-3 py-3 hover:bg-neutral-50">
      {/* Title row is the expand/collapse toggle. The meta row sits
       *  outside the <button> because it contains <Link>s to the
       *  reporter + reported users' dossiers — <a> inside <button>
       *  is invalid HTML and most browsers drop the nav. */}
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="flex w-full items-start justify-between text-left"
      >
        <p className="text-sm font-medium text-neutral-900">
          {row.reason}
          <StatusBadge status={row.status} />
          {row.relatedReportCount > 1 && (
            <Badge variant="warning" density="compact" className="ml-2">
              {row.relatedReportCount} related open
            </Badge>
          )}
        </p>
        <span className="text-xs text-neutral-400">{expanded ? '−' : '+'}</span>
      </button>
      <p className="mt-0.5 text-xs text-neutral-500">
        Filed {new Date(row.createdAt).toLocaleString()} · reporter{' '}
        <Link
          to={`/users?id=${row.reporterUserId}`}
          className="font-mono text-primary-700 hover:underline"
        >
          {row.reporterUserId.slice(0, 8)}
        </Link>{' '}
        · reported{' '}
        <Link
          to={`/users?id=${row.reportedUserId}`}
          className="font-mono text-primary-700 hover:underline"
        >
          {row.reportedUserId.slice(0, 8)}
        </Link>
      </p>

      {expanded && (
        <div className="mt-3 space-y-2 rounded-md border border-neutral-200 bg-white p-3 text-sm text-neutral-700">
          {row.note && (
            <p>
              <span className="font-medium text-neutral-900">Reporter note:</span> {row.note}
            </p>
          )}
          {row.claimedByAdminId && (
            <p>
              <span className="font-medium text-neutral-900">Claimed by:</span>{' '}
              <span className="font-mono">{row.claimedByAdminId.slice(0, 8)}</span>
              {row.claimedAt && (
                <span className="ml-1 text-neutral-500">
                  ({new Date(row.claimedAt).toLocaleString()})
                </span>
              )}
            </p>
          )}
          {canResolve && row.status === 'open' && (
            <div className="space-y-2">
              <p className="text-xs text-neutral-500">Quick resolution (no note):</p>
              <div className="flex flex-wrap gap-2">
                {(['dismiss', 'warn', 'soft_pause', 'ban'] as const).map((res) => (
                  <button
                    key={res}
                    type="button"
                    disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ reportId: row.id, resolution: res })}
                    className={`rounded-md px-3 py-1 text-xs font-medium text-white disabled:opacity-50 ${
                      res === 'ban'
                        ? 'bg-danger-600 hover:bg-danger-700'
                        : 'bg-primary-600 hover:bg-primary-700'
                    }`}
                  >
                    {res}
                  </button>
                ))}
              </div>
              {resolve.error && (
                <p className="text-sm text-danger-600">{(resolve.error as Error).message}</p>
              )}
              {resolve.data && (
                <p className="text-sm text-success-700">Resolved: {resolve.data.status}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: BadgeVariant =
    status === 'open'
      ? 'warning'
      : status === 'confirmed'
        ? 'danger'
        : status === 'dismissed'
          ? 'neutral'
          : 'info';
  return (
    <Badge variant={variant} className="ml-2">
      {status}
    </Badge>
  );
}
